import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { webhooksService } from '@/services/webhooks.service';
import { toast } from 'sonner';
import i18n from '@/i18n';

export const webhookKeys = {
  all: ['webhooks'] as const,
  list: () => [...webhookKeys.all, 'list'] as const,
  logs: (id: string) => [...webhookKeys.all, 'logs', id] as const,
};

export function useWebhooks() {
  return useQuery({
    queryKey: webhookKeys.list(),
    queryFn: () => webhooksService.findAll(),
  });
}

export function useCreateWebhook() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: webhooksService.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: webhookKeys.all });
      toast.success(i18n.t('adminIntegrations:toasts.created'));
    },
    onError: () => {
      toast.error(i18n.t('adminIntegrations:toasts.createError'));
    },
  });
}

export function useUpdateWebhook() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...dto }: { id: string; [key: string]: unknown }) =>
      webhooksService.update(id, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: webhookKeys.all });
      toast.success(i18n.t('adminIntegrations:toasts.updated'));
    },
    onError: () => {
      toast.error(i18n.t('adminIntegrations:toasts.updateError'));
    },
  });
}

export function useDeleteWebhook() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: webhooksService.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: webhookKeys.all });
      toast.success(i18n.t('adminIntegrations:toasts.deleted'));
    },
    onError: () => {
      toast.error(i18n.t('adminIntegrations:toasts.deleteError'));
    },
  });
}

export function useWebhookLogs(id: string, page = 1) {
  return useQuery({
    queryKey: [...webhookKeys.logs(id), page],
    queryFn: () => webhooksService.getLogs(id, page),
    enabled: !!id,
  });
}

export function useTestWebhook() {
  return useMutation({
    mutationFn: webhooksService.sendTest,
    onSuccess: () => {
      toast.success(i18n.t('adminIntegrations:toasts.testSent'));
    },
    onError: () => {
      toast.error(i18n.t('adminIntegrations:toasts.testError'));
    },
  });
}
